import { neverThrow } from "./util"

export enum ParentWindowType {
  OpenerTab,
  ParentFrame,
}

export type ParentWindow = {
  type: ParentWindowType
  window: Window
}

export const getParentWindow = (): ParentWindow | null => {
  return (
    neverThrow(() => {
      // If there's an opener, then the parent is a different tab/window
      if (window.opener && window.top !== window.opener.top) {
        return {
          type: ParentWindowType.OpenerTab,
          window: window.opener.top,
        }
      }

      if (window.top && window.self !== window.top) {
        return {
          type: ParentWindowType.ParentFrame,
          window: window.top,
        }
      }

      return null
    }) ?? null
  )
}

/**
 * Open a pop-up window.
 *
 * @returns A promise which resolves with the window as soon as possible, or null if the window hasn't
 * opened within 0.5 seconds
 */
export const openPopup = async (options: { url: string; name: string; features: string[] }) =>
  new Promise<Window | null>((resolve) => {
    const popup = window.open(options.url, options.name, options.features.join(","))

    let popupBlockedTimeout: NodeJS.Timeout | null = null
    let popupOpenedInterval: NodeJS.Timeout | null = null

    // Stop the timers
    const cleanup = () => {
      if (popupBlockedTimeout) {
        clearTimeout(popupBlockedTimeout)
      }

      if (popupOpenedInterval) {
        clearInterval(popupOpenedInterval)
      }
    }

    // Resolve to `null` in 0.5 seconds if popup is null
    popupBlockedTimeout = setTimeout(() => {
      if (!popup) {
        cleanup()
        resolve(null)
      }
    }, 500)

    // Resolve the popup as soon as it exists
    popupOpenedInterval = setInterval(() => {
      if (popup) {
        cleanup()
        resolve(popup)
      }
    })
  })
